// src/components/staff/StaffProfile.jsx
import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiPhone, FiCalendar, FiMapPin, FiEdit3, FiSave, FiX, FiClock, FiPlus, FiCheck, FiXCircle, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { tenantDataService } from '../../services/tenantDataService';
import { leaveService } from '../../services/leaveService';
import { calculateLeaveBalance, formatLeaveType, getStatusColor, canEditLeaveRequest, canCancelLeaveRequest } from '../../utils/leaveHelpers';
import toast from 'react-hot-toast';

const LEAVE_TYPES = [
  { value: 'leave', label: 'Annual Leave', color: 'bg-blue-100 text-blue-800' },
  { value: 'sick', label: 'Sick Leave', color: 'bg-red-100 text-red-800' },
  { value: 'personal', label: 'Personal Leave', color: 'bg-purple-100 text-purple-800' },
  { value: 'emergency', label: 'Emergency Leave', color: 'bg-orange-100 text-orange-800' },
  { value: 'maternity', label: 'Maternity/Paternity Leave', color: 'bg-green-100 text-green-800' }
];

const LEAVE_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: FiClock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: FiCheck },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: FiXCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: FiXCircle }
};

const StaffProfile = ({ staffMember, onClose, onUpdate, initialTab = 'overview' }) => {
  const { currentTenant } = useTenant();
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [leaveStats, setLeaveStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    type: 'annual',
    startDate: '',
    endDate: '',
    reason: '',
    notes: ''
  });

  // Check if current user can manage this staff member's leave
  const canManageLeave = userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  const isOwnProfile = userProfile?.uid === staffMember?.uid;

  useEffect(() => {
    if (activeTab === 'leave' && currentTenant?.id && staffMember?.id) {
      loadLeaveRequests();
    }
  }, [activeTab, currentTenant?.id, staffMember?.id]);

  const loadLeaveRequests = async () => {
    setLoading(true);
    try {
      tenantDataService.setTenantContext(currentTenant.id);

      // Load clinic settings for leave calculations
      let leaveSettings = {};
      try {
        const clinicSettings = await tenantDataService.getById('settings', 'clinic');
        leaveSettings = clinicSettings?.leaveSettings || {};
      } catch (settingsError) {
        console.warn('Could not load clinic settings, using defaults:', settingsError);
      }

      // Load leave requests using the new leave service
      const staffRequests = await leaveService.getStaffLeaveRequests(staffMember.id);
      setLeaveRequests(staffRequests);

      // Calculate leave balance and statistics using clinic-specific settings
      const balance = calculateLeaveBalance(staffMember, staffRequests, leaveSettings);
      setLeaveBalance(balance);

      // Calculate leave statistics
      const stats = await leaveService.getLeaveStatistics(staffMember.id, leaveSettings);
      setLeaveStats(stats);

    } catch (error) {
      console.error('Error loading leave requests:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!currentTenant?.id || !staffMember?.id) return;

    try {
      tenantDataService.setTenantContext(currentTenant.id);

      const leaveRequest = {
        ...leaveForm,
        staffId: staffMember.id,
        staffName: `${staffMember.firstName} ${staffMember.lastName}`,
        status: 'pending',
        requestedBy: userProfile?.uid,
        requestedByName: `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await tenantDataService.create('leaveRequests', leaveRequest);
      toast.success('Leave request submitted successfully');
      setShowLeaveForm(false);
      setLeaveForm({ type: 'annual', startDate: '', endDate: '', reason: '', notes: '' });
      loadLeaveRequests();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error('Failed to submit leave request');
    }
  };

  const handleLeaveStatusUpdate = async (requestId, newStatus, adminNotes = '') => {
    if (!canManageLeave) return;

    try {
      tenantDataService.setTenantContext(currentTenant.id);

      // Use the new leave service to update status and sync with appointment
      await leaveService.updateLeaveStatus(requestId, newStatus, adminNotes, userProfile?.uid);

      // Optimistically update the UI immediately
      setLeaveRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { ...request, status: newStatus, updatedAt: new Date().toISOString() }
            : request
        )
      );

      toast.success(`Leave request ${newStatus}`);
      
      // Dispatch global event for other components to refresh
      window.dispatchEvent(new CustomEvent('leave-status-updated', {
        detail: { requestId, newStatus, staffId: staffMember.id }
      }));
      
      // Reload data after a short delay to ensure consistency
      setTimeout(() => {
        loadLeaveRequests();
      }, 500);
    } catch (error) {
      console.error('Error updating leave request:', error);
      toast.error('Failed to update leave request');
      // Reload on error to ensure UI is consistent
      loadLeaveRequests();
    }
  };

  const handleDeleteLeaveRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this leave request?')) {
      return;
    }

    try {
      tenantDataService.setTenantContext(currentTenant.id);

      // Use the new leave service to delete request and linked appointment
      await leaveService.deleteLeaveRequest(requestId);

      toast.success('Leave request deleted');
      loadLeaveRequests();
    } catch (error) {
      console.error('Error deleting leave request:', error);
      toast.error('Failed to delete leave request');
    }
  };

  const calculateLeaveDays = (startDate, endDate) => {
    if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
      return 0;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check for invalid dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 0;
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const getLeaveStats = () => {
    const currentYear = new Date().getFullYear();
    const thisYearRequests = leaveRequests.filter(req =>
      new Date(req.startDate).getFullYear() === currentYear
    );

    const approvedRequests = thisYearRequests.filter(req => req.status === 'approved');
    const pendingRequests = thisYearRequests.filter(req => req.status === 'pending');

    const totalDaysTaken = approvedRequests.reduce((total, req) =>
      total + calculateLeaveDays(req.startDate, req.endDate), 0
    );

    const pendingDays = pendingRequests.reduce((total, req) =>
      total + calculateLeaveDays(req.startDate, req.endDate), 0
    );

    return { totalDaysTaken, pendingDays, totalRequests: thisYearRequests.length };
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FiUser className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {staffMember.firstName} {staffMember.lastName}
                </h3>
                <p className="text-gray-600 capitalize">{staffMember.role}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: FiUser },
              { id: 'leave', label: 'Leave Management', icon: FiCalendar }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-600">Email:</span> {staffMember.email}</p>
                    <p><span className="text-gray-600">Phone:</span> {staffMember.phone || 'Not provided'}</p>
                    <p><span className="text-gray-600">Date of Birth:</span> {staffMember.dateOfBirth || 'Not provided'}</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Employment Details</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-600">Role:</span> <span className="capitalize">{staffMember.role}</span></p>
                    <p><span className="text-gray-600">Department:</span> {staffMember.department || 'Not specified'}</p>
                    <p><span className="text-gray-600">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${staffMember.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {staffMember.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              {staffMember.address && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Address</h4>
                  <div className="text-sm text-gray-600">
                    <p>{staffMember.address.street}</p>
                    <p>{staffMember.address.city}, {staffMember.address.state} {staffMember.address.zipCode}</p>
                  </div>
                </div>
              )}

              {/* Emergency Contact */}
              {staffMember.emergencyContact && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Emergency Contact</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-600">Name:</span> {staffMember.emergencyContact.name || 'Not provided'}</p>
                    <p><span className="text-gray-600">Phone:</span> {staffMember.emergencyContact.phone || 'Not provided'}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'leave' && (
            <div className="space-y-6">
              {/* Leave Balance Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Annual Leave */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-blue-600 font-medium">Annual Leave</p>
                    <FiCalendar className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-blue-900">{leaveBalance?.remainingDays?.leave || 0} days left</p>
                    <p className="text-xs text-blue-600">{leaveBalance?.usedDays?.leave || 0} / {leaveBalance?.entitlements?.leave || 20} used</p>
                  </div>
                </div>

                {/* Sick Leave */}
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-red-600 font-medium">Sick Leave</p>
                    <FiUser className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-red-900">{leaveBalance?.remainingDays?.sick || 0} days left</p>
                    <p className="text-xs text-red-600">{leaveBalance?.usedDays?.sick || 0} / {leaveBalance?.entitlements?.sick || 10} used</p>
                  </div>
                </div>

                {/* Training Days */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-purple-600 font-medium">Training</p>
                    <FiClock className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-purple-900">{leaveBalance?.remainingDays?.training || 0} days left</p>
                    <p className="text-xs text-purple-600">{leaveBalance?.usedDays?.training || 0} / {leaveBalance?.entitlements?.training || 5} used</p>
                  </div>
                </div>

                {/* Remote Work */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-green-600 font-medium">Remote Work</p>
                    <FiMapPin className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-green-900">{leaveBalance?.remainingDays?.remote || 0} days left</p>
                    <p className="text-xs text-green-600">{leaveBalance?.usedDays?.remote || 0} / {leaveBalance?.entitlements?.remote || 52} used</p>
                  </div>
                </div>

                {/* Pending Requests */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-yellow-600 font-medium">Pending</p>
                    <FiClock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-yellow-900">{leaveStats?.pendingRequests || 0} requests</p>
                    <p className="text-xs text-yellow-600">Awaiting approval</p>
                  </div>
                </div>
              </div>

              {/* Leave Year Info */}
              {leaveBalance?.leaveYear && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Leave Year:</span> {' '}
                    {leaveBalance.leaveYear.start.toLocaleDateString()} - {leaveBalance.leaveYear.end.toLocaleDateString()}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">Leave Requests</h4>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={loadLeaveRequests}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiRefreshCw className="w-4 h-4" />
                    <span>Refresh</span>
                  </button>
                  {(canManageLeave || isOwnProfile) && (
                    <button
                      onClick={() => setShowLeaveForm(true)}
                      className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <FiPlus className="w-4 h-4" />
                      <span>Request Leave</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Leave Requests List */}
              <div className="bg-white border border-gray-200 rounded-lg">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading leave requests...</p>
                  </div>
                ) : leaveRequests.length === 0 ? (
                  <div className="p-8 text-center">
                    <FiCalendar className="mx-auto w-12 h-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Leave Requests</h3>
                    <p className="text-gray-600">No leave requests have been submitted yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {leaveRequests.map((request) => {
                      const leaveType = LEAVE_TYPES.find(type => type.value === request.type);
                      const statusColor = getStatusColor(request.status);
                      const days = request.daysCount || calculateLeaveDays(request.startDate, request.endDate);
                      const leaveTypeDisplay = formatLeaveType(request.type);

                      return (
                        <div key={request.id} className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${leaveType?.color || 'bg-gray-100 text-gray-800'}`}>
                                  {leaveTypeDisplay}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                </span>
                                <span className="text-sm text-gray-500">{days} day{days !== 1 ? 's' : ''}</span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <div>
                                  <p className="text-sm text-gray-600">Start Date</p>
                                  <p className="font-medium">{new Date(request.startDate).toLocaleDateString()}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600">End Date</p>
                                  <p className="font-medium">
                                    {request.endDate && request.endDate !== 'Invalid Date' && !isNaN(new Date(request.endDate).getTime())
                                      ? new Date(request.endDate).toLocaleDateString()
                                      : request.startDate && !isNaN(new Date(request.startDate).getTime())
                                        ? new Date(request.startDate).toLocaleDateString()
                                        : 'Invalid Date'
                                    }
                                  </p>
                                </div>
                              </div>

                              {request.reason && (
                                <div className="mb-3">
                                  <p className="text-sm text-gray-600">Reason</p>
                                  <p className="text-gray-900">{request.reason}</p>
                                </div>
                              )}

                              {request.adminNotes && (
                                <div className="mb-3">
                                  <p className="text-sm text-gray-600">Admin Notes</p>
                                  <p className="text-gray-900">{request.adminNotes}</p>
                                </div>
                              )}

                              <div className="text-xs text-gray-500">
                                Requested on {new Date(request.createdAt).toLocaleDateString()}
                                {request.reviewedAt && (
                                  <span> • Reviewed on {new Date(request.reviewedAt).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                              {canManageLeave && request.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleLeaveStatusUpdate(request.id, 'approved')}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Approve"
                                  >
                                    <FiCheck className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const notes = prompt('Rejection reason (optional):');
                                      if (notes !== null) {
                                        handleLeaveStatusUpdate(request.id, 'declined', notes);
                                      }
                                    }}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Decline"
                                  >
                                    <FiXCircle className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {request.autoApproved && (
                                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                  Auto-approved
                                </span>
                              )}
                              {(canManageLeave || (isOwnProfile && canEditLeaveRequest(request))) && (
                                <button
                                  onClick={() => handleDeleteLeaveRequest(request.id)}
                                  className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                  title="Delete Request"
                                  disabled={!canCancelLeaveRequest(request)}
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Leave Request Form Modal */}
        {showLeaveForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Request Leave</h3>
                  <button
                    onClick={() => setShowLeaveForm(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleLeaveSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Leave Type</label>
                  <select
                    value={leaveForm.type}
                    onChange={(e) => setLeaveForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {LEAVE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={leaveForm.startDate}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={leaveForm.endDate}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                  <textarea
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Please provide a reason for your leave request..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                  <textarea
                    value={leaveForm.notes}
                    onChange={(e) => setLeaveForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any additional information..."
                  />
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowLeaveForm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffProfile;
